/**
 * The Wikipedia paragraph, where there is one.
 *
 * Loaded straight from the browser — the API is keyless and CORS-open, so this
 * costs the worker nothing. Cached for the session, because opening the same
 * item twice should not ask twice.
 *
 * Wikipedia text is CC BY-SA, so the attribution and the link out are a licence
 * condition rather than a nicety — the same footing as the TMDb line.
 */

import { useEffect, useState } from 'react';
import { findArticle, type WikiArticle } from '../lib/wikipedia.js';
import type { WatchItem } from '../lib/model.js';

type State = { status: 'loading' } | { status: 'done'; article: WikiArticle | null };

const cache = new Map<string, WikiArticle | null>();

/**
 * What identifies the thing, rather than the row.
 *
 * A peek has no watchable id — it is a thing nobody follows yet — so keying on
 * one put every peek in the same cache slot and showed the previous item's
 * article under the next item's title, which is precisely the substitution
 * `classifyArticle` exists to prevent.
 */
const keyOf = (item: WatchItem) => `${item.source}:${item.kind}:${item.sourceId}`;

export function WikiPanel({ item }: { item: WatchItem }) {
  const cached = cache.get(keyOf(item));
  const [state, setState] = useState<State>(
    cached !== undefined ? { status: 'done', article: cached } : { status: 'loading' },
  );

  useEffect(() => {
    const hit = cache.get(keyOf(item));
    if (hit !== undefined) {
      setState({ status: 'done', article: hit });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });

    findArticle(item, controller.signal)
      .then((article) => {
        cache.set(keyOf(item), article);
        if (!controller.signal.aborted) setState({ status: 'done', article });
      })
      .catch(() => {
        // Wikipedia being unreachable is not worth an error message on a page
        // that is already showing everything the app itself knows.
        if (!controller.signal.aborted) setState({ status: 'done', article: null });
      });

    return () => controller.abort();
  }, [item.source, item.kind, item.sourceId, item.title, item.byline, item.medium]);

  if (state.status === 'loading') {
    return (
      <div className="wiki">
        <p className="wiki-label">Wikipedia</p>
        <p className="wiki-text dim">Looking…</p>
      </div>
    );
  }

  // Said plainly. An empty box would read as something failing to load, and
  // "no article" is a perfectly ordinary answer for a book announced last week.
  if (!state.article) {
    return (
      <div className="wiki">
        <p className="wiki-label">Wikipedia</p>
        <p className="wiki-text dim">Nothing on Wikipedia for this one.</p>
      </div>
    );
  }

  const { article } = state;

  return (
    <div className="wiki">
      {/* Never passed off as being about the thing when it is about the person
          who made it — the same rule the borrowed-blurb label follows. */}
      <p className="wiki-label">
        {article.about === 'creator' ? `Wikipedia · about ${article.title}` : 'Wikipedia'}
      </p>
      <div className="wiki-body">
        {article.thumbnail && (
          <img className="wiki-thumb" src={article.thumbnail} alt="" loading="lazy" />
        )}
        <p className="wiki-text">{article.extract}</p>
      </div>
      <a className="more" href={article.url} target="_blank" rel="noopener noreferrer">
        Read on Wikipedia ↗
      </a>
    </div>
  );
}
